<template>
  <fieldset aria-label="What did you watch?">
    <!-- <div class="flex justify-between items-center p-2 bg-gray-800 select-none">
      <div class="flex text-sm font-medium leading-6 text-gray-300">
        Show, Season or Episode
        <Popover class="relative">
          <PopoverButton class="focus:outline-hidden">
            <fa-icon
              icon="circle-info"
              class="ml-1 text-gray-500 transition-colors cursor-pointer hover:text-blue-300" />
          </PopoverButton>
          <PopoverPanel
            class="absolute z-10 p-4 mt-2 w-64 text-xs text-gray-800 bg-white rounded-lg border border-gray-300 shadow-lg dark:bg-gray-800 dark:text-white">
            <p>Select a season, episode or entire show to review.</p>
            <p v-if="!canModify" class="text-red-400">You can't change this</p>
          </PopoverPanel>
        </Popover>
      </div>
    </div> -->

    <div v-if="canModify" class="p-2 space-y-1 bg-gray-800 rounded">
      <span class="flex py-1 rounded-md shadow-xs isolate">
        <button
          v-for="option in options"
          :key="option.value"
          type="button"
          :class="
            reviewType === option.value
              ? 'bg-gray-200 text-gray-900 hover:bg-gray-300'
              : 'bg-gray-800 hover:bg-gray-700'
          "
          class="inline-flex relative flex-1 justify-center items-center py-2 px-2 font-semibold text-gray-100 ring-1 ring-inset ring-gray-500 cursor-pointer text-2xs md:px-3 md:text-sm focus:z-10 first:rounded-l-md last:rounded-r-md"
          @click.prevent="handleReviewTypeChange(option.value)">
          {{ option.label }}
        </button>
      </span>

      <div
        v-if="reviewType === 'tv_season' || reviewType === 'tv_episode'"
        class="flex grid grid-cols-2 items-center">
        <label
          for="season"
          class="block mb-2 text-sm font-medium text-gray-300">
          Select Season
        </label>
        <select
          v-model="tvSeason"
          class="block p-2 w-full text-sm text-gray-200 bg-gray-900 rounded-lg border border-gray-700 cursor-pointer focus:ring-blue-500 focus:border-blue-500"
          @change="handleSeasonChange">
          <template v-for="season in title.seasons" :key="season.season_number">
            <option
              v-if="season.season_number !== 0"
              :value="season.season_number">
              {{ season.name || `Season ${season.season_number}` }}
            </option>
          </template>
        </select>
      </div>

      <div
        v-if="reviewType === 'tv_episode' && tvSeason"
        class="flex grid grid-cols-2 items-center">
        <label
          for="episode"
          class="block mb-2 text-sm font-medium text-gray-300">
          Select Episode
        </label>

        <div>
          <select
            v-model="tvEpisode"
            class="block p-2 w-full text-sm text-gray-200 bg-gray-900 rounded-lg border border-gray-700 cursor-pointer focus:ring-blue-500 focus:border-blue-500">
            <option
              v-for="episode in episodes"
              :key="episode.id"
              :value="episode.episode_number">
              S{{ formatNumber(episode.season_number) }}E{{
                formatNumber(episode.episode_number)
              }}
              {{
                episode.name ||
                `Episode ${formatNumber(episode.episode_number)}`
              }}
            </option>
          </select>
        </div>
      </div>
    </div>
  </fieldset>
</template>

<script setup>
// import { Popover, PopoverButton, PopoverPanel } from '@headlessui/vue'
import { computed, ref, watch, onMounted } from 'vue'
import { useStoreTMDB } from '@/stores/storeTMDB'

const props = defineProps({
  reviewType: String,
  tvSeason: [Number, null],
  tvEpisode: [Number, null],
  titleSubtitle: String,
  title: Object,
  canModify: {
    type: Boolean,
    default: true
  }
})

const emit = defineEmits([
  'update:reviewType',
  'update:tvSeason',
  'update:tvEpisode',
  'update:titleSubtitle'
])

const options = [
  { label: 'Complete Show', value: 'tv_full' },
  { label: 'Season', value: 'tv_season' },
  { label: 'Episode', value: 'tv_episode' }
]

const storeTMDB = useStoreTMDB()

// Default to 'tv_full' if no reviewType is provided
const reviewType = ref(props.reviewType || 'tv_full')
const tvSeason = ref(props.tvSeason)
const tvEpisode = ref(props.tvEpisode)
const titleSubtitle = ref(props.titleSubtitle)

const fetchSeasonEpisodes = async () => {
  if (tvSeason.value) {
    await storeTMDB.fetchTitleTVEpisodesById(props.title.id, tvSeason.value)
  }
}

const seasonData = computed(() => {
  return tvSeason.value
    ? storeTMDB.getEpisodesByIdAndSeason(props.title.id, tvSeason.value)
    : null
})

const episodes = computed(() => {
  return seasonData.value?.data?.episodes || []
})

const currentSelectedEpisode = computed(() => {
  return episodes.value.find(
    (episode) => episode.episode_number === tvEpisode.value
  )
})

const titleSubTitleComputed = computed(() => {
  if (reviewType.value === 'tv_full') {
    return null
  }

  if (reviewType.value === 'tv_season') {
    if (!tvSeason.value) {
      return null
    }
    return `Season ${tvSeason.value}`
  }

  if (reviewType.value === 'tv_episode') {
    if (!tvSeason.value || !tvEpisode.value) {
      return null
    }
    if (currentSelectedEpisode.value) {
      return `S${formatNumber(tvSeason.value)}E${formatNumber(tvEpisode.value)} ${currentSelectedEpisode.value.name}`
    } else {
      return `S${formatNumber(tvSeason.value)}E${formatNumber(tvEpisode.value)}`
    }
  }

  return null
})

// Watchers to emit updates to parent
watch(reviewType, (newValue) => {
  emit('update:reviewType', newValue)
  if (newValue === 'tv_episode') {
    fetchSeasonEpisodes()
  }
})

watch(tvSeason, async (newValue) => {
  emit('update:tvSeason', newValue)
  if (reviewType.value === 'tv_episode') {
    await fetchSeasonEpisodes()
  }
})

watch(tvEpisode, (newValue) => {
  emit('update:tvEpisode', newValue)
})

watch(titleSubTitleComputed, (newValue) => {
  emit('update:titleSubtitle', newValue)
})

// Handle initialization of the component
onMounted(async () => {
  // Emit the default value if none was provided
  if (!props.reviewType && reviewType.value === 'tv_full') {
    emit('update:reviewType', 'tv_full')
  }

  if (reviewType.value === 'tv_episode' && tvSeason.value) {
    await fetchSeasonEpisodes()
  }
})

const reviewPropComputed = computed(() => {
  return props.reviewType
})
const tvSeasonComputed = computed(() => {
  return props.tvSeason
})
const tvEpisodeComputed = computed(() => {
  return props.tvEpisode
})

watch(reviewPropComputed, (newValue) => {
  reviewType.value = newValue
})

watch(tvSeasonComputed, (newValue) => {
  tvSeason.value = newValue
})
watch(tvEpisodeComputed, (newValue) => {
  tvEpisode.value = newValue
})

const handleReviewTypeChange = (value) => {
  reviewType.value = value

  if (reviewType.value === 'tv_full') {
    tvSeason.value = null
    tvEpisode.value = null
  }
}

const handleSeasonChange = () => {
  tvEpisode.value = null // Reset episode when season changes
}

const formatNumber = (num) => {
  return num.toString().padStart(2, '0')
}
</script>
